import type {
  MasteryLevel,
  Region,
  RiskRules,
  StudentAnalytics,
  StudentProgress,
  UserProfile,
} from "../types/domain";

export const MASTERY_THRESHOLDS = {
  practicing: 1,
  competent: 25,
  applied: 50,
  mastered: 80,
} as const;

export function masteryFromEvidence(points: number): MasteryLevel {
  if (points >= MASTERY_THRESHOLDS.mastered) return "Mastered";
  if (points >= MASTERY_THRESHOLDS.applied) return "Applied";
  if (points >= MASTERY_THRESHOLDS.competent) return "Competent";
  if (points >= MASTERY_THRESHOLDS.practicing) return "Practicing";
  return "Not Started";
}

export function calculateReward(
  base: number,
  decay: number,
  attemptNumber: number,
): number {
  return Math.floor(
    base * Math.max(0, 1 - decay * Math.max(0, attemptNumber - 1)),
  );
}

export function isRegionUnlocked(
  region: Region,
  progress: StudentProgress,
  now = new Date(),
): boolean {
  if (
    region.unlockAvailableFrom &&
    new Date(region.unlockAvailableFrom).getTime() > now.getTime()
  ) {
    return false;
  }
  const threshold = region.unlockCompletionThreshold ?? 70;
  const prerequisiteIds =
    region.unlockPrerequisiteRegionIds ?? region.prerequisiteRegionIds;
  return prerequisiteIds.every(
    (id) => (progress.regionCompletion[id] ?? 0) >= threshold,
  );
}

export function calculateLevel(totalXp: number) {
  let level = 1;
  let spent = 0;
  let next = 500;
  while (totalXp >= spent + next) {
    spent += next;
    level += 1;
    next = Math.floor(next * 1.35);
  }
  return { level, currentXp: totalXp - spent, xpNext: next };
}

export function evaluateRisk(
  student: UserProfile,
  progress: StudentProgress,
  rules: RiskRules,
  courseProgressRatio: number,
  now = new Date(),
): string[] {
  const reasons: string[] = [];
  const daysInactive =
    (now.getTime() - new Date(student.lastActiveAt).getTime()) / 86_400_000;
  const completionValues = Object.values(progress.regionCompletion);
  const completion =
    completionValues.length === 0
      ? 0
      : completionValues.reduce((sum, value) => sum + value, 0) /
        completionValues.length /
        100;
  const recentFailures = [...progress.attempts]
    .sort((a, b) => b.startedAt.localeCompare(a.startedAt))
    .slice(0, rules.recentFailureCount)
    .filter((attempt) => attempt.status === "failed").length;

  if (daysInactive >= rules.inactivityDays) {
    reasons.push(`${Math.floor(daysInactive)} days inactive`);
  }
  if (
    courseProgressRatio > 0.1 &&
    completion < courseProgressRatio * rules.expectedProgressRatio
  ) {
    reasons.push("below expected course progress");
  }
  if (recentFailures >= rules.recentFailureCount) {
    reasons.push(`${recentFailures} recent failed attempts`);
  }
  const weakestEvidence = Object.values(progress.competencyEvidence);
  if (
    courseProgressRatio > 0.35 &&
    weakestEvidence.some((points) => points < MASTERY_THRESHOLDS.competent)
  ) {
    reasons.push("core competency overdue");
  }
  return reasons;
}

export function toStudentAnalytics(
  student: UserProfile,
  progress: StudentProgress,
  rules: RiskRules,
  courseProgressRatio: number,
): StudentAnalytics {
  const regions = Object.values(progress.regionCompletion);
  const competencies = Object.values(progress.competencyEvidence);
  return {
    student,
    completion:
      regions.length === 0
        ? 0
        : Math.round(regions.reduce((sum, value) => sum + value, 0) / regions.length),
    masteryAverage:
      competencies.length === 0
        ? 0
        : Math.round(
            competencies.reduce((sum, value) => sum + value, 0) /
              competencies.length,
          ),
    attempts: progress.attempts.length,
    failedAttempts: progress.attempts.filter((a) => a.status === "failed").length,
    lastActiveAt: student.lastActiveAt,
    riskReasons: evaluateRisk(
      student,
      progress,
      rules,
      courseProgressRatio,
    ),
  };
}
